import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

declare global {
    interface Window {
        paypal?: PaypalNamespace;
    }
}

export interface PaypalNamespace {
    Buttons(config: PaypalButtonsConfig): PaypalButtonsInstance;
}

export interface PaypalButtonsConfig {
    style?: {
        layout?: 'vertical' | 'horizontal';
        color?: 'gold' | 'blue' | 'silver' | 'white' | 'black';
        shape?: 'rect' | 'pill' | 'sharp';
        label?: 'paypal' | 'checkout' | 'buynow' | 'pay' | 'installment';
        height?: number;
        disableMaxWidth?: boolean;
    };
    fundingSource?: string;
    createSubscription?: (data: unknown, actions: PaypalSubscriptionActions) => Promise<string>;
    onApprove?: (data: PaypalOnApproveData) => void | Promise<void>;
    onCancel?: (data: { subscriptionID?: string }) => void;
    onError?: (err: unknown) => void;
}

export interface PaypalSubscriptionActions {
    subscription: {
        create: (options: { plan_id: string }) => Promise<string>;
    };
}

export interface PaypalOnApproveData {
    subscriptionID?: string;
    billingToken?: string;
}

export interface PaypalButtonsInstance {
    render: (container: HTMLElement | string) => Promise<void>;
    close: () => void;
    isEligible: () => boolean;
}

@Injectable({
    providedIn: 'root',
})
export class PaypalSdkService {
    private loadPromise: Promise<PaypalNamespace> | null = null;

    get isConfigured(): boolean {
        return !!environment.paypal_client_id;
    }

    load(): Promise<PaypalNamespace> {
        if (window.paypal) {
            return Promise.resolve(window.paypal);
        }

        if (!this.isConfigured) {
            return Promise.reject(new Error('PayPal client ID is not configured'));
        }

        if (this.loadPromise) {
            return this.loadPromise;
        }

        const currency = environment.paypal_currency || 'EUR';
        const clientId = encodeURIComponent(environment.paypal_client_id);
        const params = [
            `client-id=${clientId}`,
            'components=buttons',
            'vault=true',
            'intent=subscription',
            `currency=${currency}`,
            'disable-funding=card,credit,paylater,venmo',
        ].join('&');

        this.loadPromise = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = `https://www.paypal.com/sdk/js?${params}`;
            script.async = true;
            script.onload = () => {
                if (window.paypal) {
                    resolve(window.paypal);
                    return;
                }
                reject(new Error('PayPal SDK loaded but paypal object is missing'));
            };
            script.onerror = () => reject(new Error('Failed to load PayPal SDK'));
            document.body.appendChild(script);
        });

        return this.loadPromise;
    }
}
